import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import InfiniteScrollIndicator from 'components/infinite-load-indicator/InfiniteScrollIndicator';
import Spinner from 'components/spinner/Spinner';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import WrappedPost from '../wrapped-post/WrappedPost';
import ContentListImageTile from '../image-tile/ContentListImageTile';
import ContentListResultsContext from '../../context/ContentListResultsContext';

import { downloadAttachments } from 'modules/lists-content/reducers/contentList';

import { __t, applyTranslations } from 'utils/language';
import { logError } from 'utils/log';
import { getDisplayDate } from 'utils/date';

import type { ContentListCollectionAsset, ContentListCollectionData, GalleryModalItem } from 'types';

import './content-list-image-grid.scss';

applyTranslations(require('./content-list-image-grid.i18n.json'));

type OwnProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  listId: string, // used in mapDispatchToProps,
  organizationId: number,
  isLoading: boolean,
  hasMorePages: boolean,
  hasPost: boolean,
  content: Array<ContentListCollectionData>,
  onPageLoadTrigger: () => void
};

type StateProps = {
  isAttachmentsLoading: boolean
};

type DispatchProps = {
  dispatchDownloadAttachments: (a: Array<string>) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type CampaignAssetMap = {
  [x: number]: Array<number>
};

type State = {
  selectedAssets: {
    [x: number]: boolean
  },
  selectedCampaigns: {
    [x: number]: boolean
  },
  campaignToAssets: CampaignAssetMap
};

type CustomRenderer = (b: GalleryModalItem, a: number) => React.ReactNode;

const MSG_KEY = 'content_list_image_grid';

const getCampaignToAssetIds = (content: Array<ContentListCollectionData>): CampaignAssetMap => content?.reduce((res, item) => {
  res[item.campaignId] = item.assets.map(a => a.asset.id);
  return res;
}, {});

class ContentListImageGrid extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedAssets: {},
      selectedCampaigns: {},
      campaignToAssets: getCampaignToAssetIds(props.content)
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.content !== prevProps.content) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        campaignToAssets: getCampaignToAssetIds(this.props.content)
      });
    }
  }

  handleResetSelection = () => {
    this.setState({
      selectedAssets: {},
      selectedCampaigns: {}
    });
  };

  handleToggleImageSelection = (campaignId: number, id: number) => {
    this.setState(state => {
      const updatedAssets = {
        ...state.selectedAssets,
        [id]: !state.selectedAssets[id]
      };

      const assetsIds = state.campaignToAssets[campaignId];
      const isAllSelected = assetsIds.every(i => updatedAssets[i]);

      return ({
        selectedAssets: updatedAssets,
        selectedCampaigns: {
          ...state.selectedCampaigns,
          [campaignId]: isAllSelected
        }
      });
    });
  };

  customPostRenderer = (item: GalleryModalItem, index: number): JSX.Element => {
    const { organizationId } = this.props;

    return (
      <WrappedPost
        organizationId={organizationId}
        currentPosition={index}
        postId={String(item.id)}
      />
    );
  };

  getTileCustomRenderer(): CustomRenderer | undefined | null {
    if (this.props.hasPost) {
      return this.customPostRenderer;
    }

    return undefined;
  }

  getItemImageURL(item: any): string {
    if (item.assetType === 'other') {
      return item.thumbnailUrl;
    }
    if (item.assetType === 'image') {
      return item.url;
    }

    logError(`Item of unknown type, ${item.assetType}`);
    return '';
  }

  handleToggleCampaignSelection = (campaignId: number) => {
    this.setState(state => {
      const isSelected = state.selectedCampaigns[campaignId];
      const assetsIds = state.campaignToAssets[campaignId];

      return ({
        selectedCampaigns: {
          ...state.selectedCampaigns,
          [campaignId]: !isSelected
        },
        selectedAssets: {
          ...state.selectedAssets,
          ...assetsIds.reduce((res, id) => {
            res[id] = !isSelected;
            return res;
          }, {})
        }
      });
    });
  };

  renderHeader(item: ContentListCollectionData, enableSelection?: boolean | null): React.ReactNode {
    const { selectedCampaigns } = this.state;
    const isSelected = selectedCampaigns[item.campaignId];

    return (
      <div className="content-list-image-grid__header">

        <div className="content-list-image-grid__header-name">
          <span className="content-list-image-grid__name">
            {item.campaignName}
          </span>
          {
            enableSelection ?
              <SVGDynamicIcon
                name={isSelected ? 'checkmark-inside-circle-custom-bg-white-fg' : 'checkmark-inside-circle-custom-bg'}
                width={24}
                height={24}
                customColor={isSelected ? 'primary' : 'grey'}
                onClick={() => this.handleToggleCampaignSelection(item.campaignId)}
              /> :
              null
          }
        </div>
        <div className="content-list-image-grid__header-date">
          {__t(`${MSG_KEY}.labels.starts`)}: {getDisplayDate(item.campaignStartDate)}
          &nbsp;-&nbsp;
          {__t(`${MSG_KEY}.labels.ends`)}: {getDisplayDate(item.campaignEndDate)}
        </div>
      </div>
    );
  }

  renderImageTile = (
    asset: ContentListCollectionAsset,
    campaignId: number,
    index: number,
    mappedGalleryModalItems: Array<GalleryModalItem>
  ): JSX.Element => {
    const { hasPost, onPageLoadTrigger } = this.props;
    const { selectedAssets } = this.state;

    return (
      <ContentListResultsContext.Provider
        key={asset.id}
        // @ts-ignore GalleryModalItem union type
        value={[...mappedGalleryModalItems]}
      >
        <ContentListImageTile
          index={index}
          imageUrl={this.getItemImageURL(asset)}
          canDownload={!hasPost}
          isSelected={selectedAssets[asset.id]}
          onGalleryModalLastPage={onPageLoadTrigger}
          renderCustomAsset={this.getTileCustomRenderer()}
          onToggleSelect={() => this.handleToggleImageSelection(campaignId, asset.id)}
        />
      </ContentListResultsContext.Provider>
    );
  };

  renderBar(selectedAssetsIds: Array<string>) {
    const { isAttachmentsLoading, dispatchDownloadAttachments } = this.props;

    return (
      <div className="content-list-image-grid__bar">
        <div className="content-list-image-grid__close-icon">
          <SVGDynamicIcon
            name="cross-custom-fg"
            width={18}
            height={18}
            customColor="grey"
            onClick={this.handleResetSelection}
          />
        </div>
        <div className="content-list-image-grid__bar-title">
          {__t(`${MSG_KEY}.selected`, { count: selectedAssetsIds.length })}
        </div>

        <div className="content-list-image-grid__download-icon">
          {
            isAttachmentsLoading ?
              <Spinner
                position="inline"
                size="tiny"
                color="light"
              /> :
              <Tooltip
                message={__t(`${MSG_KEY}.download_tooltip`)}
                width="macro"
                tooltipDirection={{
                  vertical: 'bottom',
                  horizontal: 'center'
                }}
              >
                <SVGDynamicIcon
                  name="download-custom-fg"
                  width={21}
                  height={21}
                  customColor="grey"
                  onClick={() => dispatchDownloadAttachments(selectedAssetsIds)}
                />
              </Tooltip>
          }
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { isLoading, hasMorePages, content, onPageLoadTrigger } = this.props;
    const { selectedAssets } = this.state;
    const selectedAssetsIds = Object.keys(selectedAssets).filter(id => selectedAssets[Number(id)]);
    const isSelectionModeEnabled = Boolean(selectedAssetsIds.length);

    const classes = classnames('content-list-image-grid', {
      'content-list-image-grid--selected': isSelectionModeEnabled
    });

    return (
      <div className={classes}>
        {
          isSelectionModeEnabled && this.renderBar(selectedAssetsIds)
        }
        {
          content.map(item => {
            const mappedGalleryModalItems: Array<GalleryModalItem> = item.assets.map(a => ({
              id: a.asset.id,
              type: 'cloudinary-image',
              assetUrl: this.getItemImageURL(a.asset)
            }));

            return (
              <React.Fragment key={`content-list-image-grid--${item.campaignName}-${item.campaignEndDate}`}>
                {this.renderHeader(item, isSelectionModeEnabled)}
                <div className="content-list-image-grid__content">
                  {
                    item.assets.map((a, index) => (
                      this.renderImageTile(a.asset, item.campaignId, index, mappedGalleryModalItems)
                    ))
                  }
                </div>
              </React.Fragment>
            );
          })
        }
        {hasMorePages && (
          <InfiniteScrollIndicator
            isLoading={isLoading}
            onLoadMore={onPageLoadTrigger}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ contentList }): StateProps => ({
  isAttachmentsLoading: contentList.isAttachmentsLoading
});

const mapDispatchToProps = (
  dispatch,
  {
    listId,
    organizationId
  }: OwnProps
): DispatchProps => ({
  dispatchDownloadAttachments: (assetsIds: Array<string>) => dispatch(downloadAttachments(organizationId, listId?.replace(/_/g, '-'), assetsIds))
});

export default connect(mapStateToProps, mapDispatchToProps)(ContentListImageGrid);
